import React, { useState } from "react";
import "./Pickup.css";

export default function Pickup() {
  const [formData, setFormData] = useState({
    donationType: 'items',
    donorName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    itemCategory: '',
    itemDescription: '',
    quantity: '',
    monetaryAmount: '',
    pickupDate: '',
    pickupTime: '',
    specialInstructions: '',
    urgency: 'normal',
    contactPreference: 'email'
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send the data to your backend
    console.log('Donation form submitted:', formData);
    setIsSubmitted(true);
  };

  if (isSubmitted) {
    return (
      <div className="donation-container">
        <div className="container">
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h2>Thank You for Your Donation!</h2>
            <p>Your donation request has been submitted successfully. We'll contact you within 24 hours to coordinate the pickup or process your monetary donation.</p>
            <div className="success-details">
              <h4>What happens next?</h4>
              <ul>
                <li>Our team will review your donation details</li>
                <li>You'll receive a confirmation email shortly</li>
                <li>We'll contact you to schedule pickup (for item donations)</li>
                <li>You'll get updates on how your donation helps the community</li>
              </ul>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                setIsSubmitted(false);
                setCurrentStep(1);
                setFormData({
                  donationType: 'items',
                  donorName: '',
                  email: '',
                  phone: '',
                  address: '',
                  city: '',
                  zipCode: '',
                  itemCategory: '',
                  itemDescription: '',
                  quantity: '',
                  monetaryAmount: '',
                  pickupDate: '',
                  pickupTime: '',
                  specialInstructions: '',
                  urgency: 'normal',
                  contactPreference: 'email'
                });
              }}
            >
              Make Another Donation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="donation-container">
      <div className="container">
        <div className="donation-header">
          <h1>Make a Donation</h1>
          <p>Help us support communities in need during crisis situations</p>

          {/* Progress Indicator */}
          <div className="progress-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Donation Type</span>
            </div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Details</span>
            </div>
            <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">Pickup & Contact</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="donation-form">
          {/* Step 1: Donation Type & Basic Info */}
          {currentStep === 1 && (
            <div className="form-step">
              <h3>Step 1: Choose Donation Type</h3>

              <div className="donation-type-selector">
                <div className="form-group">
                  <label className="radio-card">
                    <input
                      type="radio"
                      name="donationType"
                      value="items"
                      checked={formData.donationType === 'items'}
                      onChange={handleInputChange}
                    />
                    <div className="radio-content">
                      <div className="radio-icon">📦</div>
                      <h4>Donate Items</h4>
                      <p>Food, clothing, medical supplies, household items</p>
                    </div>
                  </label>
                </div>

                <div className="form-group">
                  <label className="radio-card">
                    <input
                      type="radio"
                      name="donationType"
                      value="monetary"
                      checked={formData.donationType === 'monetary'}
                      onChange={handleInputChange}
                    />
                    <div className="radio-content">
                      <div className="radio-icon">💰</div>
                      <h4>Monetary Donation</h4>
                      <p>Financial contribution to support relief efforts</p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="donorName">Full Name *</label>
                  <input
                    type="text"
                    id="donorName"
                    name="donorName"
                    value={formData.donorName}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="your.email@example.com"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
          )}

          {/* Step 2: Donation Details */}
          {currentStep === 2 && (
            <div className="form-step">
              <h3>Step 2: Donation Details</h3>

              {formData.donationType === 'items' ? (
                <>
                  <div className="form-group">
                    <label htmlFor="itemCategory">Item Category *</label>
                    <select
                      id="itemCategory"
                      name="itemCategory"
                      value={formData.itemCategory}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select category</option>
                      <option value="food">Food & Beverages</option>
                      <option value="clothing">Clothing & Textiles</option>
                      <option value="medical">Medical Supplies</option>
                      <option value="household">Household Items</option>
                      <option value="electronics">Electronics</option>
                      <option value="toys">Toys & Games</option>
                      <option value="books">Books & Educational</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="itemDescription">Item Description *</label>
                    <textarea
                      id="itemDescription"
                      name="itemDescription"
                      value={formData.itemDescription}
                      onChange={handleInputChange}
                      required
                      rows="4"
                      placeholder="Please describe the items you're donating (condition, brand, size, etc.)"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="quantity">Estimated Quantity *</label>
                    <input
                      type="text"
                      id="quantity"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., 5 bags, 20 items, 1 box"
                    />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label htmlFor="monetaryAmount">Donation Amount *</label>
                  <div className="amount-input">
                    <span className="currency">$</span>
                    <input
                      type="number"
                      id="monetaryAmount"
                      name="monetaryAmount"
                      value={formData.monetaryAmount}
                      onChange={handleInputChange}
                      required
                      min="1"
                      step="0.01"
                      placeholder="0.00"
                    />
                  </div>
                  <div className="suggested-amounts">
                    <span>Suggested amounts:</span>
                    {[25, 50, 100, 250].map(amount => (
                      <button
                        key={amount}
                        type="button"
                        className="amount-btn"
                        onClick={() => setFormData(prev => ({ ...prev, monetaryAmount: amount }))}
                      >
                        ${amount}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="urgency">Urgency Level</label>
                <select
                  id="urgency"
                  name="urgency"
                  value={formData.urgency}
                  onChange={handleInputChange}
                >
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                  <option value="emergency">Emergency</option>
                </select>
              </div>
            </div>
          )}

          {/* Step 3: Pickup & Contact Details */}
          {currentStep === 3 && (
            <div className="form-step">
              <h3>Step 3: {formData.donationType === 'items' ? 'Pickup' : 'Contact'} Information</h3>

              {formData.donationType === 'items' && (
                <>
                  <div className="form-group">
                    <label htmlFor="address">Pickup Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                      placeholder="Street address"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="city">City *</label>
                      <input
                        type="text"
                        id="city"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                        placeholder="City"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="zipCode">ZIP Code *</label>
                      <input
                        type="text"
                        id="zipCode"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        required
                        placeholder="12345"
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="pickupDate">Preferred Pickup Date</label>
                      <input
                        type="date"
                        id="pickupDate"
                        name="pickupDate"
                        value={formData.pickupDate}
                        onChange={handleInputChange}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="pickupTime">Preferred Time</label>
                      <select
                        id="pickupTime"
                        name="pickupTime"
                        value={formData.pickupTime}
                        onChange={handleInputChange}
                      >
                        <option value="">Any time</option>
                        <option value="morning">Morning (8AM - 12PM)</option>
                        <option value="afternoon">Afternoon (12PM - 5PM)</option>
                        <option value="evening">Evening (5PM - 8PM)</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div className="form-group">
                <label htmlFor="contactPreference">Preferred Contact Method</label>
                <select
                  id="contactPreference"
                  name="contactPreference"
                  value={formData.contactPreference}
                  onChange={handleInputChange}
                >
                  <option value="email">Email</option>
                  <option value="phone">Phone</option>
                  <option value="text">Text Message</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="specialInstructions">Special Instructions</label>
                <textarea
                  id="specialInstructions"
                  name="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Any special instructions for pickup or additional information..."
                />
              </div>
            </div>
          )}

          {/* Form Navigation */}
          <div className="form-navigation">
            {currentStep > 1 && (
              <button type="button" className="btn btn-secondary" onClick={handlePrev}>
                Previous
              </button>
            )}

            {currentStep < 3 ? (
              <button type="button" className="btn btn-primary" onClick={handleNext}>
                Next
              </button>
            ) : (
              <button type="submit" className="btn btn-success">
                Submit Donation
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
